import { useEffect, useSyncExternalStore } from 'react'
import { api } from '../services/API'
import type { CommitteeMember } from '../utils/types'

/**
 * O utilizador é membro em funções da comissão deste condomínio? Um só pedido
 * a `/api/comissao/minha` por condomínio, partilhado pelo menu e pelo guard de
 * rotas (o backend responde 200 com corpo vazio a quem não é membro).
 */
type Entry = { status: 'loading' | 'member' | 'not-member' }

const cache = new Map<string, Entry>()
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

function load(condominioId: string) {
  if (cache.has(condominioId)) return
  cache.set(condominioId, { status: 'loading' })
  api.get<CommitteeMember | ''>('/api/comissao/minha', { suppressForbiddenToast: true })
    .then((r) => {
      const member = r.data && typeof r.data === 'object' && r.data.activo
      cache.set(condominioId, { status: member ? 'member' : 'not-member' })
    })
    .catch(() => cache.set(condominioId, { status: 'not-member' }))
    .finally(emit)
}

const LOADING: Entry = { status: 'loading' }
const NOT_APPLICABLE: Entry = { status: 'not-member' }

export function useCommitteeMembership(condominioId: string | undefined, enabled: boolean): Entry['status'] {
  useEffect(() => {
    if (enabled && condominioId) load(condominioId)
  }, [condominioId, enabled])

  const entry = useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l) } },
    () => (!enabled || !condominioId ? NOT_APPLICABLE : cache.get(condominioId) ?? LOADING),
  )
  return entry.status
}

/** Depois de entrar/sair da comissão, força uma nova leitura. */
export function invalidateCommitteeMembership(condominioId: string) {
  cache.delete(condominioId)
  load(condominioId)
}
